import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { log } from '@/utils/logger';

/**
 * Search Store - Domain-focused state management for search and filtering
 * 
 * Responsibilities:
 * - Search query management
 * - Filter state management
 * - Search and filter operations
 * - Search history and suggestions
 * 
 * Following industry best practices:
 * - Single Responsibility Principle
 * - Immutable state updates
 * - Separation of concerns
 * - Type safety
 */

// ===== INTERFACES =====

interface ActivityFilters {
  showAnimalActivities: boolean;
  showAdventureActivities: boolean;
}

interface AdditionalFilters {
  priceRange: [number, number];
  duration: [number, number];
  groupSize: [number, number];
  hasWiFi: boolean;
  familyFriendly: boolean;
  offersMeals: boolean;
  isTourGuided: boolean;
  accessibilityFeatures: boolean;
  selectedSeason: string | null;
}

interface SearchState {
  // Search query state
  searchQuery: string;
  originalSearchQuery: string;
  
  // Search mode state
  isExplorationMode: boolean;
  isLocationSearch: boolean;
  
  // Filter state
  activityFilters: ActivityFilters;
  selectedAnimalTypes: string[];
  selectedAdventureTypes: string[];
  activeExperienceFilter: string | null;
  additionalFilters: AdditionalFilters;
  
  // Search history and suggestions
  searchHistory: string[];
  searchSuggestions: string[];
  
  // Search loading and error state
  isSearching: boolean;
  searchError: string | null;
}

interface SearchActions {
  // Search query operations
  setSearchQuery: (query: string) => void;
  setOriginalSearchQuery: (query: string) => void;
  clearSearchQuery: () => void;
  
  // Search mode operations
  setExplorationMode: (isExploring: boolean) => void;
  setLocationSearch: (isLocationSearch: boolean) => void;
  
  // Filter operations
  setActivityFilters: (filters: ActivityFilters) => void;
  setSelectedAnimalTypes: (types: string[]) => void;
  setSelectedAdventureTypes: (types: string[]) => void;
  setActiveExperienceFilter: (filter: string | null) => void;
  setAdditionalFilters: (filters: AdditionalFilters) => void;
  setSelectedSeason: (season: string | null) => void;
  
  // Combined filter operations
  clearAllFilters: () => void;
  resetFiltersToDefault: () => void;
  
  // Search history operations
  addToSearchHistory: (query: string) => void;
  clearSearchHistory: () => void;
  setSearchSuggestions: (suggestions: string[]) => void;
  
  // Search state management
  setSearching: (isSearching: boolean) => void;
  setSearchError: (error: string | null) => void;
  
  // Utility functions
  hasActiveFilters: () => boolean;
  getFilterSummary: () => string;
}

type SearchStore = SearchState & SearchActions;

// ===== INITIAL STATE =====

const initialActivityFilters: ActivityFilters = {
  showAnimalActivities: true,
  showAdventureActivities: true,
};

const initialAdditionalFilters: AdditionalFilters = {
  priceRange: [0, 1000],
  duration: [0, 480], // 0 to 8 hours in minutes
  groupSize: [1, 50],
  hasWiFi: false,
  familyFriendly: false,
  offersMeals: false,
  isTourGuided: false,
  accessibilityFeatures: false,
  selectedSeason: null,
};

const initialState: SearchState = {
  searchQuery: '',
  originalSearchQuery: '',
  isExplorationMode: false,
  isLocationSearch: false,
  activityFilters: initialActivityFilters,
  selectedAnimalTypes: [],
  selectedAdventureTypes: [],
  activeExperienceFilter: null,
  additionalFilters: initialAdditionalFilters,
  searchHistory: [],
  searchSuggestions: [],
  isSearching: false,
  searchError: null,
};

// ===== STORE IMPLEMENTATION =====

export const useSearchStore = create<SearchStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ===== SEARCH QUERY OPERATIONS =====
      
      setSearchQuery: (query: string) => {
        const currentQuery = get().searchQuery;
        
        if (currentQuery !== query) {
          set({ searchQuery: query, searchError: null }, false, 'search/setSearchQuery');
          
          // Add to history if it's a meaningful search
          if (query.length > 2 && !get().searchHistory.includes(query)) {
            const { addToSearchHistory } = get();
            addToSearchHistory(query);
          }
        }
      },

      setOriginalSearchQuery: (query: string) => {
        set({ originalSearchQuery: query }, false, 'search/setOriginalSearchQuery');
      },

      clearSearchQuery: () => {
        set({ 
          searchQuery: '', 
          originalSearchQuery: '',
          isLocationSearch: false,
          isExplorationMode: false,
          searchError: null 
        }, false, 'search/clearSearchQuery');
      },

      // ===== SEARCH MODE OPERATIONS =====
      
      setExplorationMode: (isExploring: boolean) => {
        set({ isExplorationMode: isExploring }, false, 'search/setExplorationMode');
      },

      setLocationSearch: (isLocationSearch: boolean) => {
        set({ isLocationSearch }, false, 'search/setLocationSearch');
      },

      // ===== FILTER OPERATIONS =====
      
      setActivityFilters: (filters: ActivityFilters) => {
        set({ activityFilters: filters }, false, 'search/setActivityFilters');
      },

      setSelectedAnimalTypes: (types: string[]) => {
        set({ selectedAnimalTypes: types }, false, 'search/setSelectedAnimalTypes');
      },

      setSelectedAdventureTypes: (types: string[]) => {
        set({ selectedAdventureTypes: types }, false, 'search/setSelectedAdventureTypes');
      },

      setActiveExperienceFilter: (filter: string | null) => {
        set({ activeExperienceFilter: filter }, false, 'search/setActiveExperienceFilter');
      },

      setAdditionalFilters: (filters: AdditionalFilters) => {
        set({ additionalFilters: filters }, false, 'search/setAdditionalFilters');
      },

      setSelectedSeason: (season: string | null) => {
        set(state => ({
          additionalFilters: {
            ...state.additionalFilters,
            selectedSeason: season
          }
        }), false, 'search/setSelectedSeason');
      },

      // ===== COMBINED FILTER OPERATIONS =====
      
      clearAllFilters: () => {
        set({
          activityFilters: initialActivityFilters,
          selectedAnimalTypes: [],
          selectedAdventureTypes: [],
          activeExperienceFilter: null,
          additionalFilters: initialAdditionalFilters,
        }, false, 'search/clearAllFilters');
      },

      resetFiltersToDefault: () => {
        set({
          activityFilters: initialActivityFilters,
          selectedAnimalTypes: [],
          selectedAdventureTypes: [],
          activeExperienceFilter: null,
          additionalFilters: initialAdditionalFilters,
        }, false, 'search/resetFiltersToDefault');
      },

      // ===== SEARCH HISTORY OPERATIONS =====
      
      addToSearchHistory: (query: string) => {
        const { searchHistory } = get();
        
        // Avoid duplicates and limit history size
        const newHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 10);
        
        set({ searchHistory: newHistory }, false, 'search/addToSearchHistory');
      },

      clearSearchHistory: () => {
        set({ searchHistory: [] }, false, 'search/clearSearchHistory');
      },

      setSearchSuggestions: (suggestions: string[]) => {
        set({ searchSuggestions: suggestions }, false, 'search/setSearchSuggestions');
      },

      // ===== SEARCH STATE MANAGEMENT =====
      
      setSearching: (isSearching: boolean) => {
        set({ isSearching }, false, 'search/setSearching');
      },

      setSearchError: (error: string | null) => {
        set({ searchError: error }, false, 'search/setSearchError');
      },

      // ===== UTILITY FUNCTIONS =====
      
      hasActiveFilters: () => {
        const state = get();
        return (
          state.selectedAnimalTypes.length > 0 ||
          state.selectedAdventureTypes.length > 0 ||
          state.activeExperienceFilter !== null ||
          !state.activityFilters.showAnimalActivities ||
          !state.activityFilters.showAdventureActivities ||
          state.additionalFilters.selectedSeason !== null ||
          state.additionalFilters.priceRange[0] > 0 ||
          state.additionalFilters.priceRange[1] < 1000
        );
      },

      getFilterSummary: () => {
        const state = get();
        const filters = [];
        
        if (state.selectedAnimalTypes.length > 0) {
          filters.push(`${state.selectedAnimalTypes.length} animal types`);
        }
        
        if (state.selectedAdventureTypes.length > 0) {
          filters.push(`${state.selectedAdventureTypes.length} adventure types`);
        }
        
        if (state.activeExperienceFilter) {
          filters.push(state.activeExperienceFilter);
        }
        
        if (state.additionalFilters.selectedSeason) {
          filters.push(state.additionalFilters.selectedSeason);
        }
        
        return filters.length > 0 ? filters.join(', ') : 'No filters active';
      },
    }),
    {
      name: 'search-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

// ===== SELECTOR HOOKS =====

/**
 * Optimized selectors for common use cases
 * These prevent unnecessary re-renders by only subscribing to specific data
 */

// Only re-render when search query changes
export const useSearchQuery = () => 
  useSearchStore(state => state.searchQuery);

// Only re-render when filter state changes
export const useFilters = () => 
  useSearchStore(state => ({
    activityFilters: state.activityFilters,
    selectedAnimalTypes: state.selectedAnimalTypes,
    selectedAdventureTypes: state.selectedAdventureTypes,
    activeExperienceFilter: state.activeExperienceFilter,
    additionalFilters: state.additionalFilters,
  }));

// Only re-render when search mode changes
export const useSearchMode = () => 
  useSearchStore(state => ({
    isExplorationMode: state.isExplorationMode,
    isLocationSearch: state.isLocationSearch,
  }));

// Only re-render when search history changes
export const useSearchHistory = () => 
  useSearchStore(state => ({
    history: state.searchHistory,
    suggestions: state.searchSuggestions,
  }));

// Only re-render when search loading state changes
export const useSearchLoading = () => 
  useSearchStore(state => ({
    isSearching: state.isSearching,
    error: state.searchError,
  }));

// Combined selector for filter status
export const useFilterStatus = () => 
  useSearchStore(state => ({
    hasActiveFilters: state.hasActiveFilters(),
    filterSummary: state.getFilterSummary(),
  }));

// ===== TYPE EXPORTS =====

export type { 
  SearchState, 
  SearchActions, 
  SearchStore,
  ActivityFilters,
  AdditionalFilters
}; 